import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import BasicCli from '../../../main/components/basicCli/BasicCli';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { HOTEL_CATEGORY_OPTIONS, hasHotelCategory, toggleHotelCategory } from '../../../main/constants/hotelCategories';
import IHotel from '../../../main/interfaces/IHotel';
import './Hotel.css';
import '../HotelPricing/HotelPricing.css';
import '../HotelShared/HotelShared.css';
import i18n from '../../../main/initializers/i18n';
import LocationPickerModal, { type LocationPickerValue } from '../../../main/components/locationPicker/LocationPickerModal';
import { buildEntityFileUrl } from '../../../main/utils/fileUrl';
import { compressImageForEntityType } from '../../../main/utils/imageCompression';
import { RoomTypesContent } from '../RoomTypes/RoomTypes';
import { RoomsContent } from '../Rooms/Rooms';

type HotelFormModel = IHotel & {
  note?: string;
  categories?: number;
};

interface IPagination {
  page: number;
  itemPerPage: number;
  count: number;
  itemsPerPage: number[];
}

interface IGridData {
  entity: string;
  label: string;
  isShown: boolean;
  sort?: string | null;
  filter?: {
    value?: string;
    operator?: string;
  } | null;
}

const defaultPagination: IPagination = {
  page: 1,
  itemPerPage: 10,
  count: 0,
  itemsPerPage: [10, 20, 50, 100],
};

const defaultColumns: IGridData[] = [
  { entity: 'name', label: 'Name', isShown: true },
  { entity: 'code', label: 'Code', isShown: true },
  { entity: 'address', label: 'Address', isShown: true },
  { entity: 'timezone', label: 'Timezone', isShown: true },
];

const emptyHotel: HotelFormModel = {
  id: 0,
  name: '',
  code: '',
  address: '',
  note: '',
  timezone: '',
  checkInTime: '',
  checkOutTime: '',
  categories: 0,
  currencyId: 0,
  isPublic: false,
};

// 🔥 helper
const toSlug = (name: string) =>
  name.toLowerCase().replace(/\s+/g, '-');

const getHotelId = (hotel: Partial<IHotel> | any): number => {
  const rawId = hotel?.id ?? hotel?.Id ?? 0;
  const parsedId = Number(rawId);
  return Number.isFinite(parsedId) ? parsedId : 0;
};

const HotelForm = ({
  hotel,
  selectedImageFile,
  imagePreviewUrl,
  onImageFileChange,
  onSave,
  onClose,
}: {
  hotel: HotelFormModel;
  selectedImageFile: File | null;
  imagePreviewUrl: string | null;
  onImageFileChange: (file: File | null) => void;
  onSave: (payload: HotelFormModel) => void;
  onClose: () => void;
}) => {
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<HotelFormModel>({
    defaultValues: hotel,
  });

  useEffect(() => {
    reset(hotel);
  }, [hotel, reset]);

  const selectedCategories = watch('categories') ?? 0;

  const previewUrl = useMemo(() => {
    if (selectedImageFile) {
      return URL.createObjectURL(selectedImageFile);
    }

    return imagePreviewUrl;
  }, [imagePreviewUrl, selectedImageFile]);

  useEffect(() => {
    return () => {
      if (selectedImageFile) {
        URL.revokeObjectURL(previewUrl ?? '');
      }
    };
  }, [previewUrl, selectedImageFile]);

  return (
    <form className="hotelForm" onSubmit={handleSubmit(onSave)}>
      <div className="hotelGrid">
        <GeneralInput 
        label="Name" 
        name="name" 
        type="text" 
        control={control} 
        required errors={errors}
        placeholder='Name'
        />
        


        <GeneralInput 
        label="Code"
         name="code" 
         type="text" 
         control={control} 
         required errors={errors} 
         placeholder='code'
         />
       
       
        <GeneralInput 
        label="Address" 
        name="address" 
        type="text" 
        control={control} 
        errors={errors}
        placeholder='Address'
        />
        <div className="hotelLocationPicker">
          <button type="button" className="location-picker-trigger" onClick={() => setIsLocationPickerOpen(true)}>
            {i18n.t('hotel.pickLocation', { defaultValue: 'Pick location' })}
          </button>
        </div>
        
        
        <GeneralInput label="Timezone" name="timezone" type="text" control={control} errors={errors} 
        placeholder='Timezone'/>
       
       
        <GeneralInput label="Check In Time" name="checkInTime" type="text" control={control} errors={errors} 
        placeholder='Check In Time'/>
       
       
        <GeneralInput label="Check Out Time" name="checkOutTime" type="text" control={control} errors={errors} 
        placeholder='Check Out Time'
        />
      </div>

      <div className="hotelImageField" style={{ marginTop: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
          {i18n.t('hotel.categories', { defaultValue: 'Categories' })}
        </label>
        <Controller
          name="categories"
          control={control}
          render={({ field }) => (
            <div className="hotelCategories">
              {HOTEL_CATEGORY_OPTIONS.map((category) => {
                const active = hasHotelCategory(field.value, category.id);

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => field.onChange(toggleHotelCategory(field.value, category.id))}
                    className={`hotelCategoryChip ${active ? 'isActive' : ''}`}
                  >
                    {i18n.t(category.labelKey)}
                  </button>
                );
              })}
            </div>
          )}
        />
        <div className="hotelCategoriesHint">
          {selectedCategories
            ? i18n.t('hotel.categoriesHelp', { defaultValue: 'You can select more than one category.' })
            : i18n.t('hotel.categoriesRequiredHint', { defaultValue: 'Select at least one category if you want to classify the hotel publicly.' })}
        </div>
      </div>

      <div className="hotelImageField" style={{ marginTop: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Note</label>
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              value={field.value ?? ''}
              placeholder="Note"
              rows={3}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: 12,
                border: '1px solid #ddd',
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: 15,
              }}
            />
          )}
        />
      </div>

      <div className="hotelImageField" style={{ marginTop: 16 }}>
        <Controller
          name="isPublic"
          control={control}
          render={({ field }) => (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
              <span>{i18n.t('hotel.isPublic', { defaultValue: 'Public hotel' })}</span>
            </label>
          )}
        />
      </div>

      <div className="hotelImageField" style={{ marginTop: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Cover Photo</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onImageFileChange(e.target.files?.[0] ?? null)}
        />
        {previewUrl && (
          <div style={{ marginTop: 12 }}>
            <img
              src={previewUrl}
              alt="Hotel preview"
              style={{ width: '100%', maxWidth: 360, height: 180, objectFit: 'cover', borderRadius: 12, border: '1px solid #ddd' }}
            />
          </div>
        )}
      </div>

      <div className="hotelActions">
        <Button variant="outlined" onClick={onClose}>
          {i18n.t("generic.cancel")}
        </Button>
        <Button type="submit" variant="contained">
          {i18n.t("generic.save")}
        </Button>
      </div>
      <LocationPickerModal
        open={isLocationPickerOpen}
        title={i18n.t('hotel.pickLocation', { defaultValue: 'Pick location' })}
        initialQuery={hotel.address ?? ''}
        onClose={() => setIsLocationPickerOpen(false)}
        onSelect={(value: LocationPickerValue) => {
          setValue('address', value.label, { shouldDirty: true, shouldTouch: true });
        }}
      />
    </form>
  );
};

const Hotel = () => {
  const [activeTab, setActiveTab] = useState<'management' | 'roomType' | 'rooms'>('management');
  const [hotels, setHotels] = useState<HotelFormModel[]>([]);
  const [pagination, setPagination] = useState<IPagination>(defaultPagination);
  const [gridItems, setGridItems] = useState<IGridData[]>(defaultColumns);
  const [selectedHotel, setSelectedHotel] = useState<HotelFormModel>(emptyHotel);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const navigate = useNavigate();

  const loadHotels = useCallback(async (pg?: IPagination, items?: IGridData[]) => {
    try {
      const request = {
        pagination: pg ?? defaultPagination,
        gridItems: items ?? defaultColumns,
      };

      const res = await axiosInstance.post(ApiConstants.hotel.hotelListCt, request);
      setHotels(
        (res.data.items || []).map((item: any) => ({
          ...item,
          categories: Number(item?.categories ?? item?.Categories ?? 0),
        }))
      );
      setPagination({ ...defaultPagination, ...(res.data.pagination || {}) });
      setGridItems(res.data.gridItems || defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to load hotels');
    }
  }, []);

  useEffect(() => {
    loadHotels(defaultPagination, defaultColumns);
  }, [loadHotels]);

  useEffect(() => {
    const fetchHotelImage = async () => {
      if (!openModal || !selectedHotel.id) {
        setImagePreviewUrl(null);
        return;
      }

      try {
        setImagePreviewUrl(buildEntityFileUrl(selectedHotel.id, ApiConstants.EntityTypes.Hotel));
      } catch {
        setImagePreviewUrl(null);
      }
    };

    void fetchHotelImage();

    return () => {
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
    };
  }, [openModal, selectedHotel.id]);

  const handleHotelImageFileChange = async (file: File | null) => {
    if (!file) {
      setSelectedImageFile(null);
      return;
    }

    const compressedFile = await compressImageForEntityType(file, ApiConstants.EntityTypes.Hotel);
    setSelectedImageFile(compressedFile);
  };

  const handleSave = async (payload: HotelFormModel) => {
    try {
      const saveResponse = await axiosInstance.post(ApiConstants.hotel.hotelSave, payload);
      const savedHotel = saveResponse.data as IHotel;
      const hotelId = getHotelId(savedHotel) || getHotelId(payload);

      if (selectedImageFile && hotelId > 0) {
        const formData = new FormData();
        formData.append('file', selectedImageFile);
        formData.append('hotelId', String(hotelId));

        await axiosInstance.post(ApiConstants.hotel.hotelImageUpload, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success(payload.id ? 'Hotel updated' : 'Hotel created');
      setOpenModal(false);
      setSelectedHotel(emptyHotel);
      setSelectedImageFile(null);
      loadHotels(defaultPagination, defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to save hotel');
    }
  };

  const handleDelete = async (item: IHotel) => {
    if (!window.confirm(`Delete hotel ${item.name}?`)) return;

    try {
      await axiosInstance.delete(ApiConstants.hotel.hotelDelete, {
        params: { id: item.id },
      });
      toast.success('Hotel deleted');
      loadHotels(defaultPagination, defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to delete hotel');
    }
  };

  // 🔥 navigation handler
  const handleOpenReservations = (hotel: IHotel) => {
   const slug = hotel.name.toLowerCase().replace(/\s+/g, '-');
navigate(`/reservations/${slug}`);
  };

  const hotelInfoTabs = (
    <div className="hotelPricingTabs hotelInfoTabs">
      <button
        type="button"
        className={`hotelPricingTab ${activeTab === 'management' ? 'active' : ''}`}
        onClick={() => setActiveTab('management')}
      >
        Hotel Management
      </button>
      <button
        type="button"
        className={`hotelPricingTab ${activeTab === 'roomType' ? 'active' : ''}`}
        onClick={() => setActiveTab('roomType')}
      >
        Room Type
      </button>
      <button
        type="button"
        className={`hotelPricingTab ${activeTab === 'rooms' ? 'active' : ''}`}
        onClick={() => setActiveTab('rooms')}
      >
        Hotel Rooms
      </button>
    </div>
  );

  return (
    <BasicCli>
      <div className="hotelSectionPage">
        <div className="ui-panel hotelSectionHeader">
          <div className="hotelSectionHeading">
            <h2>{i18n.t("hotel.title")}</h2>
          </div>

        </div>
        {activeTab === 'management' ? (
          <div className="hotelInfoTabPanel">
        <div className="ui-panel hotelSectionHeader hotelInfoEmbeddedHeader">
          <div className="hotelInfoEmbeddedRow">
            <div className="hotelInfoEmbeddedTabs">{hotelInfoTabs}</div>
            <div className="hotelSectionActions hotelInfoActions">
              <Button
                variant="contained"
                className="hotelInfoPrimaryAction"
                onClick={() => {
                  setSelectedHotel(emptyHotel);
                  setOpenModal(true);
                }}
              >
                {i18n.t("hotel.addHotel")}
              </Button>
            </div>
          </div>
        </div>
        <div className="bcCard hotelSectionCard hotelTableCard">
          <GeneralTable
            data={hotels}
            title={i18n.t("hotel.hotelList")}
            hasRp
            columns={gridItems}
            gridEntity={12}
            pagination={pagination}
            selectedItem={(item) => handleOpenReservations(item)} // 🔥 CLICK ROW → NAVIGATE
            selectedItemPagination={(pg) => loadHotels(pg, gridItems)}
            onEdit={(item) => {
              setSelectedHotel({
                ...emptyHotel,
                ...item,
                categories: Number((item as any).categories ?? (item as any).Categories ?? 0),
              });
              setOpenModal(true);
            }}
            onDelete={handleDelete}
          />
        </div>
          </div>
        ) : activeTab === 'roomType' ? (
          <div className="hotelInfoTabPanel">
            <RoomTypesContent embedded tabsBar={hotelInfoTabs} />
          </div>
        ) : (
          <div className="hotelInfoTabPanel">
            <RoomsContent embedded tabsBar={hotelInfoTabs} />
          </div>
        )}

        {openModal && (
          <GeneralModal
            handleClose={() => setOpenModal(false)}
            headerName={
              selectedHotel.id
                ? i18n.t("hotel.editHotel")
                : i18n.t("hotel.createHotel")
            }
            width="900px"
            radius="16px"
            hideFooter
          >
            <HotelForm
              hotel={selectedHotel}
              selectedImageFile={selectedImageFile}
              imagePreviewUrl={imagePreviewUrl}
              onImageFileChange={handleHotelImageFileChange}
              onSave={handleSave}
              onClose={() => setOpenModal(false)}
            />
          </GeneralModal>
        )}
      </div>
    </BasicCli>
  );
};

export default Hotel;
